/**
 * NBA RTDB Configuration
 * Centralized configuration for NBA Firebase Realtime Database caching
 */

// Firebase RTDB instance for NBA
export const NBA_RTDB_URL = 'https://just-rhythm-445002-s8-e92d0-2d6f5.firebaseio.com/';

// Cache paths structure
export const CACHE_PATHS = {
  // Root path for all NBA cache
  ROOT: 'cache/nba',

  // Games caching paths
  GAMES: 'cache/nba/games',
  GAMES_DATES: 'cache/nba/games/dates',
  GAMES_LIVE: 'cache/nba/games/live',

  // Other data types
  STANDINGS: 'cache/nba/standings',
  PLAYERS: 'cache/nba/players',
  TEAMS: 'cache/nba/teams',

  // Metadata
  STATS: 'cache/nba/metadata/stats',
  POPULAR: 'cache/nba/metadata/popular'
};

// TTL configurations (in milliseconds)
export const TTL_CONFIG = {
  // Live data - very short TTL
  LIVE_GAMES: 30 * 1000,           // 30 seconds

  // Game data - moderate TTL
  DATE_GAMES: 5 * 60 * 1000,       // 5 minutes
  UPCOMING_GAMES: 10 * 60 * 1000,  // 10 minutes

  // Completed games - longer TTL
  PAST_GAMES: 60 * 60 * 1000,      // 1 hour
  HISTORICAL_GAMES: 24 * 60 * 60 * 1000, // 24 hours

  // Standings and stats - longer TTL
  STANDINGS: 6 * 60 * 60 * 1000,   // 6 hours
  CONFERENCE_STANDINGS: 6 * 60 * 60 * 1000, // 6 hours

  // Static data - very long TTL
  TEAMS: 24 * 60 * 60 * 1000,      // 24 hours
  PLAYERS: 12 * 60 * 60 * 1000,    // 12 hours
  PLAYER_STATS: 6 * 60 * 60 * 1000, // 6 hours

  // Default fallback
  DEFAULT: 5 * 60 * 1000           // 5 minutes
};

// Cache key generation helpers
export const getCacheKey = (type, ...params) => {
  // Sanitize parameters to create valid Firebase keys
  const sanitized = params
    .filter(p => p !== null && p !== undefined)
    .map(p => String(p).replace(/[.#$\[\]]/g, '_'))
    .join('_');

  return sanitized;
};

// Determine TTL based on game status
// NBA API uses numeric status codes: 1=Not Started, 2=Live, 3=Finished
export const getTTLForGame = (game) => {
  const status = game?.status?.short;

  // Live game - status short = 2
  if (status === 2) {
    return TTL_CONFIG.LIVE_GAMES;
  }

  // Completed game - status short = 3
  if (status === 3) {
    // Check if game is from today
    const gameDate = game?.date?.start?.split('T')[0];
    const today = new Date().toISOString().split('T')[0];

    if (gameDate === today) {
      return TTL_CONFIG.PAST_GAMES;
    }
    return TTL_CONFIG.HISTORICAL_GAMES;
  }

  // Upcoming games - status short = 1
  if (status === 1) {
    return TTL_CONFIG.UPCOMING_GAMES;
  }

  // Postponed (4), Delayed (5), Canceled (6)
  if (status === 4 || status === 5 || status === 6) {
    return TTL_CONFIG.PAST_GAMES;
  }

  // Default
  return TTL_CONFIG.DEFAULT;
};

// Debug flag
export const DEBUG_CACHE = process.env.NODE_ENV === 'development';

// Logging helper
export const logCache = (level, message, data = null) => {
  if (DEBUG_CACHE) {
    const prefix = '🏀 [NBA-RTDB]';
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];

    switch (level) {
      case 'hit':
        console.log(`${prefix} ✅ ${timestamp} - ${message}`, data || '');
        break;
      case 'miss':
        console.log(`${prefix} ❌ ${timestamp} - ${message}`, data || '');
        break;
      case 'set':
        console.log(`${prefix} 💾 ${timestamp} - ${message}`, data || '');
        break;
      case 'error':
        console.error(`${prefix} 🚨 ${timestamp} - ${message}`, data || '');
        break;
      case 'info':
      default:
        console.log(`${prefix} ℹ️ ${timestamp} - ${message}`, data || '');
        break;
    }
  }
};

// Helper to determine if we should use cache based on data freshness
export const shouldUseCache = (cacheData) => {
  if (!cacheData) return false;

  const { ttl, lastUpdated } = cacheData;
  const now = Date.now();
  const age = now - lastUpdated;

  return age < ttl;
};

// Popular NBA teams for pre-warming cache
export const POPULAR_TEAMS = [
  { id: 17, name: 'Los Angeles Lakers' },
  { id: 11, name: 'Golden State Warriors' },
  { id: 4, name: 'Brooklyn Nets' },
  { id: 2, name: 'Boston Celtics' },
  { id: 20, name: 'Miami Heat' },
  { id: 21, name: 'Milwaukee Bucks' },
  { id: 28, name: 'Phoenix Suns' },
  { id: 14, name: 'Houston Rockets' }
];

// Export all configurations
export default {
  NBA_RTDB_URL,
  CACHE_PATHS,
  TTL_CONFIG,
  getCacheKey,
  getTTLForGame,
  DEBUG_CACHE,
  logCache,
  shouldUseCache,
  POPULAR_TEAMS
};
